import { api } from '@/convex/_generated/api';
import { useMutation } from 'convex/react';
import { Id } from '@/convex/_generated/dataModel';
import { ConvexClient } from 'convex/browser';
import { diagnosticFailureClass } from '@/lib/value-free-diagnostics';

// Initialize a Convex client
const client = new ConvexClient(import.meta.env.VITE_CONVEX_URL || '');

interface Web3AuthUser {
	email: string;
	name?: string;
	sub?: string;
}

type CreateUserMutation = (args: {
	email: string;
	name: string;
	role: string;
	clerkId: string;
}) => Promise<Id<'users'> | { _id: Id<'users'>; [key: string]: unknown }>;

type UpdateLastLoginMutation = (args: { userId: Id<'users'> }) => Promise<void>;

/**
 * Creates a new user in the Convex database if they don't already exist
 *
 * @param web3AuthUser - The user info from Web3Auth
 * @param createUserMutation - The Convex mutation function to create a user
 * @returns Promise with the user ID or null if creation failed
 */
export const syncUserWithConvex = async (
	web3AuthUser: Web3AuthUser,
	createUserMutation: CreateUserMutation
) => {
	if (!web3AuthUser || !web3AuthUser.email) {
		console.error('Cannot sync user: Missing user info from Web3Auth');
		return null;
	}

	try {
		// Extract user info from Web3Auth
		const {
			email,
			name = email.split('@')[0],
			sub: clerkId,
		} = web3AuthUser;

		// Create user in Convex if they don't exist
		// Assuming the create function handles checking if user already exists
		const userId = await createUserMutation({
			email,
			name: name || email.split('@')[0], // Use first part of email if no name
			role: 'user', // Default role - adjust as needed
			clerkId: clerkId || 'web3auth_' + Date.now(), // Generate ID if none provided
		});

		return userId;
	} catch (error) {
		console.error('Error syncing user with Convex:', {
			failureClass: diagnosticFailureClass(error),
		});
		return null;
	}
};

/**
 * Hook to get Convex mutations for user management
 * NOTE: This hook must be used inside components under ConvexProvider
 */
export const useUserMutations = () => {
	const updateLastLogin = useMutation(api.users.updateLastLogin);

	return {
		updateLastLogin,

		// Helper to sync user after Web3Auth login
		syncUser: async (_web3AuthUser: Web3AuthUser) => {
			throw new Error('SELF_REGISTRATION_REQUIRES_SERVER_IDENTITY');
		},
	};
};

// Direct mutation functions that don't require hooks
// (These functions require the mutation function to be passed in)
export const directMutations = {
	syncUser: syncUserWithConvex,

	updateLastLogin: async (
		updateLastLoginMutation: UpdateLastLoginMutation,
		userId: Id<'users'>
	) => {
		try {
			await updateLastLoginMutation({ userId });
			return true;
		} catch (error) {
			console.error('Error updating last login:', {
				failureClass: diagnosticFailureClass(error),
			});
			return false;
		}
	},

	// Generic query function for direct database access
	query: async ({
		tableName,
		queryName,
		queryArgs,
	}: {
		database?: string;
		tableName: string;
		queryName: string;
		queryArgs: { email: string };
	}) => {
		try {
			// Map the tableName to the correct module in the API
			if (tableName === 'users' && queryName === 'getByEmail') {
				const result = await client.query(
					api.users.getByEmail,
					queryArgs as { email: string }
				);
				return result;
			}

			// Add more table mappings here as needed
			console.error('Unsupported direct query:', {
				failureClass: 'UNSUPPORTED_DIRECT_QUERY',
			});
			return null;
		} catch (error) {
			console.error('Error in direct query:', {
				failureClass: diagnosticFailureClass(error),
			});
			return null;
		}
	},

	users: {
		getByEmail: async ({ email }: { email: string }) => {
			try {
				// Use the Convex client to query directly
				const result = await client.query(api.users.getByEmail, {
					email,
				});
				return result;
			} catch (error) {
				console.error('Error in direct getByEmail:', {
					failureClass: diagnosticFailureClass(error),
				});
				return null;
			}
		},
	},

	// Get user by email
	getByEmail: async ({ email }: { email: string }) => {
		try {
			return await client.query(api.users.getByEmail, { email });
		} catch (error) {
			console.error('Error getting user by email:', {
				failureClass: diagnosticFailureClass(error),
			});
			return null;
		}
	},

	// Check if user needs wallet setup
	checkWalletSetup: async ({ userId }: { userId: Id<'users'> }) => {
		return await client.query(api.users.checkNeedsWalletSetup, { userId });
	},

	// Trigger token distribution for user
	triggerDistribution: async ({
		userId,
		walletAddress,
	}: {
		userId: Id<'users'>;
		walletAddress: string;
	}) => {
		try {
			// Use the new scheduleTokenDistribution function which handles the distribution immediately
			await client.mutation(
				api.tokens.scheduleTokenDistribution,
				{
					userId,
					walletAddress,
				}
			);
			return true;
		} catch (error) {
			console.error('Error scheduling token distribution:', {
				failureClass: diagnosticFailureClass(error),
			});
			return false;
		}
	},
};
